import { useMemo, useRef, useState } from 'react';

interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  up?: boolean;
  strokeWidth?: number;
  fill?: boolean;
  animate?: boolean;
}

export const Sparkline = ({
  data,
  width = 120,
  height = 36,
  up = true,
  strokeWidth = 1.6,
  fill = true,
  animate = true,
}: SparklineProps) => {
  const gradId = useMemo(() => `sg-${Math.random().toString(36).slice(2, 8)}`, []);
  if (!data || data.length === 0) return null;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const stepX = width / (data.length - 1);
  const points = data.map(
    (v, i) => [i * stepX, height - ((v - min) / range) * (height - 4) - 2] as [number, number],
  );
  const d = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(2)},${p[1].toFixed(2)}`)
    .join(' ');
  const dFill = `${d} L${width},${height} L0,${height} Z`;
  const color = up ? 'var(--tk-up)' : 'var(--tk-down)';

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ display: 'block' }}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {fill && <path d={dFill} fill={`url(#${gradId})`} />}
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={animate ? 'tk-spark-path' : ''}
      />
    </svg>
  );
};

interface DonutSlice {
  value: number;
  color: string;
}

interface DonutProps {
  slices: DonutSlice[];
  size?: number;
  thickness?: number;
}

export const DonutAllocation = ({ slices, size = 140, thickness = 16 }: DonutProps) => {
  const total = slices.reduce((s, x) => s + x.value, 0);
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="var(--tk-line)"
        strokeWidth={thickness}
      />
      {slices.map((s, i) => {
        const len = (s.value / total) * c;
        const dash = `${len} ${c - len}`;
        const dashOff = -offset;
        offset += len;
        return (
          <circle
            key={i}
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={s.color}
            strokeWidth={thickness}
            strokeDasharray={dash}
            strokeDashoffset={dashOff}
            strokeLinecap="butt"
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: 'stroke-dasharray 0.8s var(--tk-ease)' }}
          />
        );
      })}
    </svg>
  );
};

interface AreaChartProps {
  data: number[];
  /** Parallel array of epoch-ms timestamps for hover tooltips. Optional —
   *  when omitted, the tooltip just shows the value with a "point N" label. */
  timestamps?: number[];
  width?: number;
  height?: number;
  /** Force the trend color. When omitted, derived from first vs last point of
   *  the series — so a 1M chart that's net-positive over the period reads
   *  green even if today's tick happens to be red. */
  up?: boolean;
}

export const AreaChart = ({ data, timestamps, width = 720, height = 220, up }: AreaChartProps) => {
  const gradId = useMemo(() => `ag-${Math.random().toString(36).slice(2, 8)}`, []);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  // Span detection: if the timestamps cover > 1 day, the tooltip shows date.
  // Otherwise (intraday only), it shows time. Saves a "Mon" label on a 1D chart.
  const hasTimestamps = timestamps && timestamps.length === data.length;
  const tsSpansMultiDay =
    hasTimestamps && timestamps[timestamps.length - 1] - timestamps[0] > 22 * 60 * 60 * 1000;
  const fmt = useMemo(
    () =>
      tsSpansMultiDay
        ? new Intl.DateTimeFormat('en-PK', {
            day: 'numeric',
            month: 'short',
            timeZone: 'Asia/Karachi',
          })
        : new Intl.DateTimeFormat('en-PK', {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'Asia/Karachi',
          }),
    [tsSpansMultiDay],
  );
  if (!data || data.length === 0) return null;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const padTop = 10;
  const padBottom = 24;
  const usableH = height - padTop - padBottom;
  const stepX = data.length > 1 ? width / (data.length - 1) : width;
  const points = data.map(
    (v, i) => [i * stepX, padTop + (1 - (v - min) / range) * usableH] as [number, number],
  );
  const d = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(2)},${p[1].toFixed(2)}`)
    .join(' ');
  const dFill = `${d} L${width},${height - padBottom} L0,${height - padBottom} Z`;
  const derivedUp = data[data.length - 1] >= data[0];
  const showUp = up ?? derivedUp;
  const color = showUp ? 'var(--tk-up)' : 'var(--tk-down)';
  const gridY = [0.25, 0.5, 0.75].map((p) => padTop + p * usableH);
  const lastPoint = points[points.length - 1];

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current || data.length === 0) return;
    const rect = svgRef.current.getBoundingClientRect();
    const xRel = (e.clientX - rect.left) / rect.width; // 0..1
    const xSvg = xRel * width;
    const idx = Math.max(0, Math.min(data.length - 1, Math.round(xSvg / stepX)));
    setHoverIdx(idx);
  };



  const hovered = hoverIdx !== null ? points[hoverIdx] : null;
  const hoveredValue = hoverIdx !== null ? data[hoverIdx] : null;
  const hoveredTs = hoverIdx !== null && hasTimestamps ? timestamps[hoverIdx] : null;

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <svg
        ref={svgRef}
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        style={{ display: 'block' }}
        onMouseMove={onMove}
        onMouseLeave={() => setHoverIdx(null)}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.22" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {gridY.map((y, i) => (
          <line
            key={i}
            x1="0"
            x2={width}
            y1={y}
            y2={y}
            stroke="var(--tk-line-2)"
            strokeWidth="1"
            strokeDasharray="2 4"
          />
        ))}
        <path d={dFill} fill={`url(#${gradId})`} />
        <path
          d={d}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="tk-spark-path"
        />
        {/* Latest-point marker — always visible. */}
        <circle cx={lastPoint[0]} cy={lastPoint[1]} r="4" fill={color} />
        <circle cx={lastPoint[0]} cy={lastPoint[1]} r="8" fill={color} fillOpacity="0.18" />
        {hovered && (
          <>
            <line
              x1={hovered[0]}
              x2={hovered[0]}
              y1={padTop}
              y2={height - padBottom}
              stroke="var(--tk-ink-3)"
              strokeWidth="1"
              strokeDasharray="3 3"
              opacity="0.5"
            />
            <circle cx={hovered[0]} cy={hovered[1]} r="5" fill="var(--tk-bg)" stroke={color} strokeWidth="2" />
          </>
        )}
      </svg>
      {hovered && hoveredValue !== null && (
        <div
          style={{
            position: 'absolute',
            // Snap toward the side nearest the data point so the tooltip
            // doesn't fall off either edge.
            left: `${(hovered[0] / width) * 100}%`,
            top: 0,
            transform:
              hovered[0] / width < 0.15
                ? 'translate(8px, 0)'
                : hovered[0] / width > 0.85
                  ? 'translate(calc(-100% - 8px), 0)'
                  : 'translate(-50%, 0)',
            background: 'var(--tk-surface)',
            border: '1px solid var(--tk-line)',
            borderRadius: 'var(--tk-radius-sm)',
            boxShadow: 'var(--tk-shadow-sm)',
            padding: '6px 10px',
            fontSize: 12,
            fontFamily: 'var(--tk-mono)',
            color: 'var(--tk-ink)',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            lineHeight: 1.4,
          }}
        >
          {hoveredTs !== null && (
            <div style={{ color: 'var(--tk-ink-3)', fontSize: 10.5, letterSpacing: '0.04em' }}>
              {fmt.format(new Date(hoveredTs))}
            </div>
          )}
          <div style={{ fontWeight: 700 }}>
            {hoveredValue.toLocaleString('en-PK', { maximumFractionDigits: 2 })}
          </div>
        </div>
      )}
    </div>
  );
};
